/**
 * Admin "Lead backup" panel — surfaces the daily off-Emergent CSV/XLSX backup
 * scheduler. Two controls: run an on-demand backup right now, and view the
 * history of past runs (recipient, counts per collection, file size, Resend
 * delivery id).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso || "—"; }
};

const PrettyKey = ({ k }) => (
  <span className="text-zinc-700">{k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())}</span>
);

export default function LeadBackupPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/backup/history");
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load backup history");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      if (data.sent) {
        toast.success(`Backup sent · ${data.total} rows · ${data.recipient}`);
      } else {
        toast.warning(data.reason || "Backup built but email skipped (RESEND_API_KEY not set)");
      }
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backup failed");
    } finally { setRunning(false); }
  };

  return (
    <div data-testid="admin-lead-backup-panel">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Lead backup</h2>
          <p className="text-zinc-500 text-[13px] mt-1">
            Off-Emergent XLSX dump of every lead the site has captured (5 sheets: assessment leads, portfolio leads, newsletter, contact, brochure). Runs automatically at 03:00 UTC daily and is emailed to <code className="text-[12px] text-zinc-700">NOTIFICATION_EMAIL</code>.
          </p>
        </div>
        <button
          type="button"
          onClick={runNow}
          disabled={running}
          className="ek-btn ek-btn-primary text-[12.5px] disabled:opacity-50"
          data-testid="lead-backup-run-now"
        >
          <Send size={13} /> {running ? "Building & sending…" : "Run backup now"}
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase text-zinc-500">Recent runs</h3>
        <button type="button" onClick={reload} className="text-zinc-500 hover:text-black text-[12px]" data-testid="lead-backup-refresh">
          <RefreshCw size={12} className="inline mr-1" /> Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-zinc-500 text-[13px]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 text-center">
          <p className="text-zinc-600 text-[14px]">No backup runs yet. Click <strong>Run backup now</strong> to send the first one.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-zinc-200">
          <table className="min-w-full text-[13px]">
            <thead className="bg-zinc-50 text-left text-zinc-500 text-[11px] uppercase tracking-[0.12em]">
              <tr>
                <th className="p-3">Generated</th>
                <th className="p-3">Recipient</th>
                <th className="p-3">Status</th>
                <th className="p-3">Total rows</th>
                <th className="p-3">Per collection</th>
                <th className="p-3">Size</th>
                <th className="p-3">Resend id</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-zinc-100 align-top hover:bg-zinc-50/50" data-testid={`backup-run-${it.id}`}>
                  <td className="p-3 text-zinc-700 whitespace-nowrap">{fmtDate(it.generated_at)}</td>
                  <td className="p-3 text-zinc-700">{it.recipient || "—"}</td>
                  <td className="p-3">
                    <span className={`inline-block ${it.sent ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"} text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5`}>
                      {it.sent ? "Sent" : "Skipped"}
                    </span>
                  </td>
                  <td className="p-3 tabular-nums font-semibold">{it.total}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1 max-w-[320px]">
                      {Object.entries(it.counts || {}).map(([k, v]) => (
                        <span key={k} className="inline-block bg-[#F4EEFD] text-[#7737F6] text-[10.5px] px-1.5 py-0.5 border border-[#7737F6]/30">
                          <PrettyKey k={k} /> · <strong>{v}</strong>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 tabular-nums text-zinc-600">{it.file_size_kb} KB</td>
                  <td className="p-3 font-mono text-[10.5px] text-zinc-500 break-all max-w-[180px]">{it.email_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
